/**
 * La medición de un elemento del plano (bloque o capa) a cantidades: «asignables» de la selección y la FOTO del
 * generador de un elemento. Vive FUERA de `PlanosPage` para que la misma cuenta la usen la pantalla del plano y
 * la comparación de revisiones («Cambió el plano»): una sola regla, sin copias. Funciones puras: todo lo que
 * antes leían del estado de la página entra por el contexto.
 */
import type { CapasClasificadasDTO, DecisionDeCapa, PlanoCapa, PlanoInventario, PlanoSeleccion } from '@supernova/shared';
import {
  agruparVanosPorTipo,
  capaSeMideComoMuro,
  categoriaIfcSingular,
  cuentaDeMuroConVanos,
  decisionDeBloque,
  decisionDeCapa,
  decisionIfc as decisionIfcDeClase,
  describirTipoIfcParaSugerencia,
  fotoDeMedicion,
  medidaDeCapaDwg,
  muestraLargoIfc,
  nombreLegibleTipoIfc,
  nombrePiezaIfc,
  type CapaDwgParaMedir,
  type FotoGenerador,
  type TipoDeVano,
  type TramoDeMuro,
  type VanoDelPlano,
} from '@supernova/shared';
import type { PlanoMagnitudConcepto } from '../api/planos.js';
import { etiquetasDeNiveles } from './nivelesIfc.js';
import { sumarCapa } from './medicionIfc.js';


/**
 * La cuenta del muro con sus vanos (bruto − Σ ancho × alto). Los tramos con alto propio (cada planta con
 * el suyo) lo usan si TODAS lo tienen; si no, todos usan el alto general. null = falta el alto.
 */
export function cuentaDeVanosDe(
  mv: Asignable['muroVanos'] | undefined,
  altoStr: string,
  altosVanosStr: Readonly<Record<string, string>>,
): ReturnType<typeof cuentaDeMuroConVanos> | null {
  if (!mv) return null;
  const general = Number(altoStr.replace(',', '.'));
  const todasConAlto = mv.tramos.every((t) => t.altoPropioM != null);
  if (!todasConAlto && !(general > 0)) return null;
  const tramos: TramoDeMuro[] = mv.tramos.map((t) => ({
    ejeBrutoM: t.ejeBrutoM,
    altoM: todasConAlto ? t.altoPropioM! : general,
    vanos: t.vanos,
  }));
  const altos: Record<string, number | null> = {};
  for (const t of mv.tipos) {
    const n = Number((altosVanosStr[t.clave] ?? '').replace(',', '.'));
    altos[t.clave] = Number.isFinite(n) && n > 0 ? n : null;
  }
  return cuentaDeMuroConVanos(tramos, mv.factor, altos);
}

/** Una selección del plano lista para asignarle un concepto (bloque o capa). */
export interface Asignable {
  key: string; // `${tipo}:${clave}` — identidad estable
  seleccion: PlanoSeleccion;
  etiqueta: string;
  detalle: string;
  cantidadDefault: number;
  /** Nota sobre cómo se midió la cantidad (solo capas: longitud leída de la geometría). */
  notaMedicion?: string;
  /** true si la cantidad por default es una longitud medida en metros (capa con escala real). */
  medidoEnMetros?: boolean;
  /** Área de recintos CERRADOS de la capa en m² (piso/losa: m² directo del plano, sin altura). */
  areaM2?: number;
  /** Espesor sugerido (m) del muro de doble trazo detectado, para pre-llenar el m³. */
  espesorSugerido?: number;
  /** true = la medición automática es dudosa (confianza media) → marcar el renglón para revisar. */
  revisar?: boolean;
  /** Nivel/planta del modelo BIM (IFC), para el eje "Niveles" de la HP. Solo IFC lo trae. */
  nivel?: string | null;
  /** Ubicación/zona del modelo BIM (IFC, IfcSpace), para el eje "Ubicación / zona". Solo IFC lo trae. */
  ubicacion?: string | null;
  /**
   * Cantidades REALES del modelo BIM (IFC): área/longitud/volumen/conteo ya medidos. Cuando están,
   * la cantidad sale directa del modelo según la unidad del concepto (sin pedir alto/espesor, que el
   * DWG 2D sí necesita). Solo IFC lo trae.
   */
  medidas?: NonNullable<PlanoCapa['medidas']>;
  /**
   * Selección por instancia (IFC/BIM): cuántas piezas del tipo están incluidas de cuántas hay. Cuando
   * `sel < total` el usuario sacó piezas en el visor 3D y las medidas de arriba ya se recalcularon solo
   * con las incluidas. Solo se puebla cuando hay instancias.
   */
  instSel?: { sel: number; total: number };
  /** true = la capa/bloque está marcado como RUIDO (no se presupuesta) → no se le puede asignar concepto. */
  esRuido?: boolean;
  /** DWG: la capa es un muro medido por su eje (doble trazo): su área es largo × alto, no la de sus anillos. */
  esMuroDobleTrazo?: boolean;
  /**
   * DWG con varias plantas: cada planta que suma, con su largo, su altura (y de dónde sale) y su área.
   * Solo en la capa COMPLETA (sin vista elegida) y solo si la capa aparece en 2+ plantas.
   */
  porPlanta?: MedidaDePlanta[];
  /**
   * Área de muro (m²) de la capa completa sumando cada planta con SU altura (Σ largo × alto). null si
   * a alguna planta le falta la altura (entonces se pide un alto único, como siempre).
   */
  areaMuroPorPlantas?: number | null;
  /** DWG: la vista elegida (una planta) y su altura con su origen. */
  vista?: { id: string; rotulo: string; altoM: number | null; origen: string };
  /** DWG: vistas que no suman (fachadas, cortes) en las que esta capa también está dibujada. */
  vistasQueNoSuman?: string[];
  /** DWG: la escala es dudosa y el usuario todavía no dijo la unidad → no se muestra ninguna medida. */
  sinEscala?: boolean;
  /**
   * DWG, muro de doble trazo con vanos: los tramos (una planta, o el plano si no tiene vistas) con su
   * eje BRUTO en metros, su alto propio (el de la planta; null = el alto general del campo) y sus vanos;
   * y los tipos de vano (bloque + ancho) a los que se les pide el alto una vez.
   */
  muroVanos?: {
    tramos: { ejeBrutoM: number; altoPropioM: number | null; vanos: VanoDelPlano[] }[];
    tipos: TipoDeVano[];
    /** Factor del dibujo a metros (para los anchos de los vanos). */
    factor: number;
  };
}

/**
 * Los tramos de un muro con vanos (una planta, o las plantas que suman, o el plano entero) en metros, y
 * sus tipos de vano. Lo usan el panel (asignables) y la lista de capas: el mismo armado en los dos.
 * undefined si algún tramo no tiene eje bruto o no hay vanos.
 */
export function armarMuroVanos(
  fuentes: { eje?: number; vanos?: { ancho: number; bloque: string | null; dibujado?: boolean }[]; altoPropioM: number | null }[],
  factor: number,
): Asignable['muroVanos'] {
  if (fuentes.length === 0 || !fuentes.every((s) => s.eje != null && s.eje > 0)) return undefined;
  const tramos = fuentes.map((s) => ({
    ejeBrutoM: s.eje! * factor,
    altoPropioM: s.altoPropioM,
    vanos: (s.vanos ?? []).map((v) => ({ ancho: v.ancho, bloque: v.bloque, ...(v.dibujado ? { dibujado: true } : {}) })),
  }));
  const tipos = agruparVanosPorTipo(tramos.flatMap((t) => t.vanos), factor);
  return tipos.length > 0 ? { tramos, tipos, factor } : undefined;
}

/** Una planta de un DWG con varias vistas, para el desglose de una capa. */
export interface MedidaDePlanta {
  vista: string;
  rotulo: string;
  largoM: number | null;
  altoM: number | null;
  origen: string;
  areaM2: number | null;
}

/** Rótulo de una vista para mostrar: el que trae el dibujo, en tipo oración ("PLANTA BAJA" → "Planta baja"). */
export function rotuloDeVista(v: { id: string; rotulo: string | null } | undefined, i?: number): string {
  const r = v?.rotulo?.trim();
  if (!r) return `Vista ${i != null ? i + 1 : v?.id ?? ''}`.trim();
  const t = r.toLocaleLowerCase('es-MX');
  return t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);
}

/**
 * Mide una capa 2D (o una capa dentro de una vista) en metros. La decisión (muro de doble trazo, qué
 * área encierra, si hay que revisarla) la toma el lector de planos con los umbrales de Calibración; acá
 * solo se aplica con el factor de la pantalla (paso 3: una sola medición, `medidaDeCapaDwg`).
 */
export function medirCapa2D(m: CapaDwgParaMedir, factor: number | null, esMuroSemantico: boolean) {
  return medidaDeCapaDwg(m, factor, esMuroSemantico);
}

/**
 * Decisión por default de un elemento IFC a partir de su CATEGORÍA estándar (IfcWall, IfcFurnishing…).
 * El IFC no pasa por el clasificador de capas por IA (ya viene tipado), así que la cubeta se deduce de
 * la clase del elemento: mobiliario / anotación / espacios / ejes / aberturas no se presupuestan; el
 * resto es obra. El usuario siempre puede corregir con el ⋯ (decisionOverride manda sobre esto).
 */
export function decisionIfc(categoria?: string | null, supertipos?: readonly string[] | null): DecisionDeCapa {
  // La regla vive en @supernova/shared (por la clase y sus ancestros del esquema): mobiliario, espacios,
  // anotaciones, vanos → solo dibujo; genéricos y entorno → "Revisar: genéricos"; lo demás, obra.
  return decisionIfcDeClase(categoria, supertipos);
}

/** Lo que `asignablesDelPlano` necesita del plano abierto (en la pantalla, del estado de la página). */
export interface ContextoAsignables {
  inventario: PlanoInventario | null | undefined;
  factorEscala: number | null;
  capasClasificadas: CapasClasificadasDTO | null;
  decisionOverride: Record<string, DecisionDeCapa>;
  alturaPorVista: Map<string, { altoM: number | null; origen: string }>;
  escalaSinResolver: boolean;
}

/** Lo que `fotoDeElemento` necesita: lo anterior (sin alturas ni escala por resolver) más los niveles y las piezas. */
export interface ContextoFoto extends Pick<ContextoAsignables, 'inventario' | 'factorEscala' | 'capasClasificadas' | 'decisionOverride'> {
  nivelDeElemento: Map<number, number>;
  etiquetaNivel: ReturnType<typeof etiquetasDeNiveles>;
  /** Cada pieza del modelo IFC por su expressID (su clase y su caja). */
  piezaDelModelo: Map<number, { clase: string | null; dims?: { largo: number; ancho: number; alto: number } }>;
}

/**
 * Qué selecciones hay activas (bloque o capa) y su medición. Se arma para la selección del visor y, con
 * «Armar el presupuesto», para cada tipo que falta sin seleccionarlo: la MISMA medición en los dos caminos.
 */
export function asignablesDelPlano(
  ctx: ContextoAsignables,
  capasSel: ReadonlySet<string>,
  idsSel: ReadonlySet<string>,
  instExcluidas: ReadonlySet<number>,
  vistaSel: { capa: string; vista: string } | null,
): Asignable[] {
  const { inventario, factorEscala, capasClasificadas, decisionOverride, alturaPorVista, escalaSinResolver } = ctx;

  const out: Asignable[] = [];
  // ¿La capa/bloque quedó en RUIDO? (corrección del usuario si la hay; si no, la de la IA para DWG o
  // la de la categoría estándar para IFC). El ruido no se presupuesta → no se le asigna concepto.
  const esRuidoDe = (clave: string, categoria: string | null | undefined, esBloque: boolean): boolean => {
    const d =
      decisionOverride[clave] ??
      (esBloque
        ? decisionDeBloque(clave, capasClasificadas)
        : categoria != null
          ? decisionIfc(categoria)
          : decisionDeCapa(clave, capasClasificadas));
    return d === 'ruido';
  };
  // Bloques con instancias seleccionadas → cantidad por default = conteo.
  if (inventario) {
    for (const b of inventario.bloques) {
      const ids = b.ids.filter((id) => idsSel.has(id));
      if (ids.length === 0) continue;
      out.push({
        key: `bloque:${b.nombre}`,
        seleccion: { tipo: 'bloque', clave: b.nombre, ids },
        etiqueta: b.nombre,
        detalle: `${ids.length} de ${b.n} pieza${b.n === 1 ? '' : 's'}`,
        cantidadDefault: ids.length,
        esRuido: esRuidoDe(b.nombre, null, true),
      });
    }
  }
  // Capas seleccionadas → cantidad por default = LONGITUD MEDIDA de la capa, en metros.
  // Fase 2 (ml de muros): la geometría YA está medida por el conversor (suma de longitudes
  // de las polilíneas de la capa, en unidades del dibujo); acá se convierte a metros con la
  // escala real del plano. La escala sale de la unidad ELEGIDA por el usuario (selector),
  // que por default es la que declara el DWG ($INSUNITS → factor_a_metro) pero es corregible
  // porque el header no es confiable. Si no se conoce la escala o la capa no tiene longitud
  // medible, se cae al default 1 y el usuario pone la cantidad a mano.
  const factor = factorEscala;
  for (const capa of capasSel) {
    const info = inventario?.capas.find((c) => c.capa === capa);
    // Naturaleza de la capa según la IA (independiente del idioma). null = sin clasificación →
    // comportamiento IDÉNTICO al de hoy. Si la IA la excluyó de "muro" (ejes/mobiliario/
    // instalaciones = 'otro', o vano/ventana/anotación), NO se propone como muro de doble trazo.
    // La corrección del ⋯ manda (capaSeMideComoMuro): «Es solo dibujo» la saca; «Se construye» la rescata.
    const esMuroSemantico = capaSeMideComoMuro(capa, capasClasificadas, decisionOverride[capa]);
    // IFC (BIM): las cantidades vienen MEDIDAS del modelo (área/longitud/volumen/conteo). No hay
    // que derivar nada con la altura; la cantidad final la elige la unidad del concepto (más abajo,
    // en cantidadCalculada). El default pre-concepto es el área si existe, si no la longitud, si no
    // el conteo — la magnitud más probable de cobro de ese elemento.
    if (info?.medidas) {
      // Las medidas de la capa son la suma de TODAS las instancias del tipo. Si el usuario sacó
      // instancias en el visor 3D (`instExcluidas`), se rehace la suma SOLO con las incluidas: sacar
      // 1 de 13 → conteo 12 y su área/longitud/volumen de menos. Sin instancias (inventario viejo) o
      // sin exclusiones, la suma es la del modelo tal cual.
      const insts = info.instancias ?? [];
      const total = insts.length || info.medidas.conteo;
      let m: NonNullable<PlanoCapa['medidas']>;
      let selN = total;
      if (insts.length > 0 && instExcluidas.size > 0) {
        const incluidas = insts.filter((x) => !instExcluidas.has(x.id));
        selN = incluidas.length;
        // La MISMA suma que el tipo entero (`sumarCapa`): piezas exactas, total redondeado, y con el
        // peso de las incluidas (antes, con una selección parcial, se mostraba el peso del tipo entero).
        m = sumarCapa(incluidas, selN);
      } else {
        m = info.medidas;
      }
      const parcial = selN < total;
      const estimado = info.medidas?.estimadoGeometria === true;
      // expressID de las instancias INCLUIDAS → se persisten en la selección del renglón (JSONB), para
      // saber después QUÉ piezas cubre cada concepto y pintar el estado de asignación por pieza. Si el
      // inventario no trae instancias (IFC viejo) se omite y el concepto se toma como el tipo entero.
      const idsIncluidas =
        insts.length > 0 ? insts.filter((x) => !instExcluidas.has(x.id)).map((x) => String(x.id)) : undefined;
      const partes = [
        `${m.conteo} pza${parcial ? ` de ${total}` : ''}`,
        m.area != null ? `${m.area.toLocaleString('es-MX')} m²` : null,
        m.longitud != null && muestraLargoIfc(info?.categoria, info?.ifc?.supertipos, info?.medidas?.manda) ? `${m.longitud.toLocaleString('es-MX')} m` : null,
        m.volumen != null ? `${m.volumen.toLocaleString('es-MX')} m³` : null,
      ].filter(Boolean);
      out.push({
        key: `capa:${capa}`,
        seleccion: { tipo: 'capa', clave: capa, ids: idsIncluidas },
        etiqueta: `Capa: ${capa}`,
        detalle: partes.join(' · '),
        esRuido: esRuidoDe(capa, info?.categoria, false),
        cantidadDefault: m.area ?? m.longitud ?? m.conteo,
        areaM2: m.area,
        medidas: m,
        instSel: insts.length > 0 ? { sel: selN, total } : undefined,
        // Nivel/zona leídos del modelo BIM (IFC) para los ejes de agrupación etapa 2.
        nivel: info.nivel,
        ubicacion: info.ubicacion,
        medidoEnMetros: m.area != null || m.longitud != null,
        notaMedicion:
          (parcial
            ? `${selN} de ${total} piezas seleccionadas en el modelo BIM. `
            : '') +
          (estimado
            ? 'Cantidades ESTIMADAS de la geometría (el modelo no las declara): revísalas. La cantidad se ajusta a la unidad del concepto.'
            : 'Cantidades leídas del modelo BIM. La cantidad se ajusta a la unidad del concepto.'),
      });
      continue;
    }
    // DWG con varias vistas: la capa puede medirse SOLO en una planta (vistaSel) o completa (las
    // plantas que suman). Las fachadas y cortes nunca suman (el lector ya las dejó fuera del total).
    const vistasInv = inventario?.vistas ?? [];
    const vistaInfo = (id: string) => vistasInv.find((v) => v.id === id);
    const idxVista = (id: string) => vistasInv.findIndex((v) => v.id === id);
    const pvElegida =
      vistaSel?.capa === capa ? info?.por_vista?.find((x) => x.vista === vistaSel.vista) : undefined;
    const med = medirCapa2D(pvElegida ?? info ?? {}, factor, esMuroSemantico);
    const { metros, areaM2, dobleTrazo, corridoM, espesorSugerido, revisar, baseLargo, longitudUnid } = med;
    const alturaDePlanta = (id: string) =>
      alturaPorVista.get(id) ?? { altoM: null, origen: 'el plano no trae una fachada o un corte con esta altura: escríbela' };
    // Desglose por planta (solo capa completa, y solo si la capa está en 2+ plantas que suman).
    const plantasDeCapa = (info?.por_vista ?? []).filter((x) => vistaInfo(x.vista)?.suma === true);
    const porPlanta: MedidaDePlanta[] | undefined =
      !pvElegida && plantasDeCapa.length >= 2
        ? plantasDeCapa.map((x) => {
            const m = medirCapa2D(x, factor, esMuroSemantico);
            // Alto y área por planta solo en un muro (medido por su eje); en otra capa no significan nada.
            const h = m.dobleTrazo ? alturaDePlanta(x.vista) : { altoM: null, origen: '' };
            const largo = m.baseLargo;
            return {
              vista: x.vista,
              rotulo: rotuloDeVista(vistaInfo(x.vista), idxVista(x.vista)),
              largoM: largo,
              altoM: h.altoM,
              origen: h.origen,
              areaM2: largo != null && h.altoM != null ? Math.round(largo * h.altoM * 100) / 100 : null,
            };
          })
        : undefined;
    const areaMuroPorPlantas =
      porPlanta && porPlanta.every((x) => x.areaM2 != null)
        ? Math.round(porPlanta.reduce((sum, x) => sum + (x.areaM2 ?? 0), 0) * 100) / 100
        : null;
    const vistasQueNoSuman = (info?.por_vista ?? [])
      .filter((x) => vistaInfo(x.vista) && vistaInfo(x.vista)!.suma === false && x.n_entidades > 0)
      .map((x) => rotuloDeVista(vistaInfo(x.vista), idxVista(x.vista)));
    const vistaDeTarget = pvElegida
      ? { id: pvElegida.vista, rotulo: rotuloDeVista(vistaInfo(pvElegida.vista), idxVista(pvElegida.vista)), ...alturaDePlanta(pvElegida.vista) }
      : undefined;
    const nEnt = info
      ? `${pvElegida?.n_entidades ?? info.n_entidades} entidad${(pvElegida?.n_entidades ?? info.n_entidades) === 1 ? '' : 'es'}`
      : 'capa seleccionada';
    // Muro con vanos: una planta → sus vanos; la capa completa → los de todas las plantas que suman
    // (cada una con su alto); un plano sin vistas → los de la capa.
    let muroVanos: Asignable['muroVanos'];
    if (dobleTrazo && factor != null && factor > 0 && info) {
      const fuentes: { eje?: number; vanos?: PlanoCapa['vanos']; altoPropioM: number | null }[] = pvElegida
        ? [{ eje: pvElegida.eje_bruto, vanos: pvElegida.vanos, altoPropioM: null }]
        : plantasDeCapa.length > 0
          ? plantasDeCapa.map((x) => ({
              eje: x.eje_bruto,
              vanos: x.vanos,
              altoPropioM: plantasDeCapa.length >= 2 ? alturaDePlanta(x.vista).altoM : null,
            }))
          : [{ eje: info.eje_bruto, vanos: info.vanos, altoPropioM: null }];
      muroVanos = armarMuroVanos(fuentes, factor);
    }
    out.push({
      key: pvElegida ? `capa:${capa}@${pvElegida.vista}` : `capa:${capa}`,
      seleccion: pvElegida ? { tipo: 'capa', clave: capa, vista: pvElegida.vista } : { tipo: 'capa', clave: capa },
      etiqueta: vistaDeTarget ? `${capa} · ${vistaDeTarget.rotulo}` : `Capa: ${capa}`,
      esRuido: esRuidoDe(capa, info?.categoria, false),
      detalle: dobleTrazo
        ? `${nEnt} · eje ${corridoM!.toLocaleString('es-MX')} m${revisar ? ' (revisar)' : ''}`
        : metros != null
          ? `${nEnt} · ${metros.toLocaleString('es-MX')} m medidos`
          : nEnt,
      cantidadDefault: baseLargo != null && baseLargo > 0 ? baseLargo : 1,
      medidoEnMetros: baseLargo != null,
      areaM2,
      espesorSugerido,
      revisar,
      esMuroDobleTrazo: dobleTrazo,
      porPlanta,
      areaMuroPorPlantas,
      vista: vistaDeTarget,
      vistasQueNoSuman: vistasQueNoSuman.length > 0 ? vistasQueNoSuman : undefined,
      sinEscala: escalaSinResolver,
      muroVanos,
      notaMedicion: escalaSinResolver
        ? 'Todavía no sé en qué unidad está dibujado este plano. Contesta la pregunta de arriba del dibujo y aquí aparecen sus medidas.'
        : dobleTrazo
          ? `Este muro está dibujado con dos líneas: medí su largo por el centro (${corridoM} m), no sumando las dos caras (${metros} m)` +
            `${espesorSugerido ? `; su ancho es de unos ${espesorSugerido} m` : ''}.` +
            `${revisar ? ' No estoy del todo seguro de esa medida: el renglón entrará marcado para que lo revises.' : ''}`
          : metros != null
            ? 'Largo de las líneas de esta capa, leído del plano. Revísalo: si los muros están dibujados con dos líneas, cuenta las dos caras.'
            : longitudUnid > 0
              ? 'El plano no dice en qué unidad está dibujado, así que no pude medirlo en metros. Escribe la cantidad a mano.'
              : 'Esta capa no tiene líneas que se puedan medir. Escribe la cantidad a mano.',
    });
  }
  return out;
}


/**
 * Números generadores: la FOTO de la medición de un elemento con la unidad del concepto (la MISMA cuenta
 * que la cantidad: `cantidadParaConcepto`), desglosada por pieza (IFC, con su nivel) o por planta (DWG,
 * con sus vanos). null = el plano no da la cantidad con esa unidad.
 */
export function fotoDeElemento(
  ctx: ContextoFoto,
  a: Asignable,
  o: {
    magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined;
    unidadConcepto: string | null | undefined;
    altoStr: string;
    espesorStr: string;
    altosVanos: Readonly<Record<string, string>>;
    altoDeDonde?: string | null;
  },
): FotoGenerador | null {
  const { inventario, nivelDeElemento, etiquetaNivel, factorEscala, capasClasificadas, decisionOverride, piezaDelModelo } = ctx;

  const info = inventario?.capas.find((c) => c.capa === a.seleccion.clave);
  const esIfc = !!a.medidas;
  const nombre = esIfc && info?.categoria ? nombreLegibleTipoIfc(info) : a.seleccion.clave;
  const elemento = { tipo: a.seleccion.tipo, clave: a.seleccion.clave, ...(a.seleccion.vista ? { vista: a.seleccion.vista } : {}) };
  const ifcTipo = esIfc && info ? describirTipoIfcParaSugerencia(info) : null;
  const vanos = cuentaDeVanosDe(a.muroVanos, o.altoStr, o.altosVanos);
  // IFC: cada pieza incluida, con su nivel (el del modelo) y su nombre tal como viene.
  // El nivel con su número y altura y, si el modelo le da otro nombre, ese nombre («Nivel 1 · +0.00 m (Erdgeschoss)»).
  const nombreNivel = (eid: number) => {
    const n = nivelDeElemento.get(eid);
    const et = n != null ? etiquetaNivel.get(n) : undefined;
    if (!et) return info?.nivel ?? 'Sin nivel';
    return et.nombreDelModelo && et.nombreDelModelo !== et.titulo && et.nombreDelModelo !== et.corto ? `${et.titulo} (${et.nombreDelModelo})` : et.titulo;
  };
  const insts = esIfc ? info?.instancias ?? [] : [];
  const incluidas = a.seleccion.ids?.length ? new Set(a.seleccion.ids) : null;
  const piezasIfc = insts
    .map((x, i) => ({ x, i }))
    .filter(({ x }) => !incluidas || incluidas.has(String(x.id)))
    .map(({ x, i }) => ({
      id: String(x.id),
      nombre: nombrePiezaIfc({ nombre: x.nombre, clase: info?.categoria, indice: i }),
      grupo: nombreNivel(x.id),
      area: x.area,
      longitud: x.longitud,
      volumen: x.volumen,
      peso: x.peso,
      estimadas: x.magnitudesEstimadas,
      ...(x.dims ? { dims: x.dims } : {}),
      // Lo que llena sus vanos (el modelo lo dice), con su ancho y alto: el número generador lo descuenta.
      ...(x.alojados?.length
        ? {
            vanos: x.alojados.flatMap((id) => {
              const v = piezaDelModelo.get(id);
              return v?.dims ? [{ nombre: categoriaIfcSingular(v.clase), ancho: v.dims.largo, alto: v.dims.alto }] : [];
            }),
          }
        : {}),
    }))
    .sort((p, q) => (p.grupo === 'Sin nivel' ? 1 : 0) - (q.grupo === 'Sin nivel' ? 1 : 0) || p.grupo.localeCompare(q.grupo, 'es', { numeric: true }));
  // DWG: lo que mide la capa (o el bloque) en cada planta que suma.
  const vistas = inventario?.vistas ?? [];
  const rotulo = (id: string) => rotuloDeVista(vistas.find((v) => v.id === id), vistas.findIndex((v) => v.id === id));
  const esMuro = !esIfc && a.seleccion.tipo === 'capa' && capaSeMideComoMuro(a.seleccion.clave, capasClasificadas, decisionOverride[a.seleccion.clave]);
  const bloque = a.seleccion.tipo === 'bloque' ? inventario?.bloques.find((b) => b.nombre === a.seleccion.clave) : undefined;
  const plantas = esIfc
    ? undefined
    : a.porPlanta
      ? a.porPlanta.map((p) => {
          const pv = info?.por_vista?.find((x) => x.vista === p.vista);
          return {
            vista: p.vista,
            rotulo: p.rotulo,
            largoM: p.largoM,
            altoM: p.altoM,
            deDondeAlto: p.origen,
            areaPisoM2: pv ? medirCapa2D(pv, factorEscala, esMuro).areaM2 ?? null : null,
          };
        })
      : bloque?.por_vista
        ? Object.entries(bloque.por_vista)
            .filter(([v, n]) => n > 0 && vistas.find((x) => x.id === v)?.suma === true)
            .map(([v, n]) => ({ vista: v, rotulo: rotulo(v), largoM: null, altoM: null, piezas: n }))
        : undefined;
  // Muro con vanos: cada tramo con el alto con que lo cuenta la hoja (el suyo si todos lo tienen).
  const mv = a.muroVanos;
  const general = Number(o.altoStr.replace(',', '.'));
  const todasConAlto = !!mv && mv.tramos.every((t) => t.altoPropioM != null);
  const nombresTramo = a.vista ? [{ rotulo: a.vista.rotulo, vista: a.vista.id }] : a.porPlanta?.map((p) => ({ rotulo: p.rotulo, vista: p.vista })) ?? [];
  const altosPorTipo: Record<string, number | null> = {};
  for (const t of mv?.tipos ?? []) {
    const n = Number((o.altosVanos[t.clave] ?? '').replace(',', '.'));
    altosPorTipo[t.clave] = Number.isFinite(n) && n > 0 ? n : null;
  }
  const muro =
    mv && (todasConAlto || general > 0)
      ? {
          factor: mv.factor,
          altosPorTipo,
          tramos: mv.tramos.map((t, i) => ({
            rotulo: nombresTramo.length === mv.tramos.length ? nombresTramo[i]!.rotulo : a.vista?.rotulo ?? '',
            ...(nombresTramo.length === mv.tramos.length && !a.vista ? { vista: nombresTramo[i]!.vista } : {}),
            ejeBrutoM: t.ejeBrutoM,
            altoM: todasConAlto ? t.altoPropioM! : general,
            vanos: t.vanos,
          })),
        }
      : null;
  return fotoDeMedicion({
    magnitud: o.magnitud,
    unidadConcepto: o.unidadConcepto ?? undefined,
    cantidadDefault: a.cantidadDefault,
    baseEsConteo: a.seleccion.tipo === 'bloque',
    medidas: a.medidas,
    mandaIfc: info?.medidas?.manda,
    areaM2: a.areaM2,
    sinEscala: a.sinEscala,
    areaMuroPorPlantas: a.areaMuroPorPlantas,
    netoVanosM2: vanos?.netoM2 ?? null,
    altoStr: o.altoStr,
    espesorStr: o.espesorStr,
    elemento,
    nombre,
    grupo: a.vista?.rotulo ?? (esIfc ? info?.nivel ?? '' : ''),
    unidad: o.unidadConcepto ?? null,
    piezasIfc: esIfc ? piezasIfc : undefined,
    plantas,
    muro,
    altoDeDonde: o.altoDeDonde ?? null,
    uso: ifcTipo ? `${ifcTipo.clase}|${ifcTipo.predefinido ?? ''}` : null,
  });
}

/**
 * Cuántas puertas y ventanas aloja el elemento: las que el plano dibuja en el muro (DWG) o las que el modelo le
 * aloja a sus piezas (IFC). Lo que va en cada vano (refuerzo, dintel, cabezal) sale de aquí. null = no se sabe.
 */
export function vanosDelElemento(a: Asignable, capas: readonly PlanoCapa[] | undefined): number | null {
  if (a.muroVanos) {
    const n = a.muroVanos.tramos.reduce((t, x) => t + x.vanos.length, 0);
    return n > 0 ? n : null;
  }
  if (a.medidas && a.seleccion.tipo === 'capa') {
    const info = capas?.find((c) => c.capa === a.seleccion.clave);
    const incluidas = a.seleccion.ids?.length ? new Set(a.seleccion.ids) : null;
    const n = (info?.instancias ?? [])
      .filter((x) => !incluidas || incluidas.has(String(x.id)))
      .reduce((t, x) => t + (x.alojados?.length ?? 0), 0);
    return n > 0 ? n : null;
  }
  return null;
}

/** Lo que `magnitudesDelPrincipal` necesita además del elemento (en la pantalla, del estado de la página). */
export interface EntradaMagnitudes {
  altoStr: string;
  espesorStr: string;
  /** La cuenta del muro con sus vanos (`cuentaDeVanosDe`) con el alto vigente; null si no es un muro con vanos. */
  cuentaVanos: { netoM2: number | null } | null;
  /** Eje bruto de todos los tramos del muro (con vanos), en m; null si no hay. Ver `largoConVanosDe`. */
  largoConVanosM: number | null;
  capas?: readonly PlanoCapa[];
}

/** Largo del muro contando sus vanos (Σ del eje bruto de cada tramo), redondeado al mm. null = no es un muro con vanos. */
export function largoConVanosDe(muroVanos: Asignable['muroVanos'] | undefined): number | null {
  const tr = muroVanos?.tramos;
  return tr && tr.length > 0 ? Math.round(tr.reduce((s, t) => s + t.ejeBrutoM, 0) * 1000) / 1000 : null;
}

/**
 * Las magnitudes de un elemento (conteo, largo, área, volumen, vanos) con las que un complementario deriva su
 * cantidad: la MISMA cuenta que la del principal. La usan el panel y «Cambió el plano» (con la medición de la
 * revisión nueva): una sola regla.
 */
export function magnitudesDelPrincipal(target: Asignable, o: EntradaMagnitudes) {
  const md = target.medidas; // IFC (BIM): magnitudes ya medidas del modelo
  const alto = Number(o.altoStr.replace(',', '.'));
  const espesor = Number(o.espesorStr.replace(',', '.'));
  const esBloque = target.seleccion.tipo === 'bloque';
  // La base de una capa 2D (DWG/DXF) en metros es su LONGITUD medida (o el corrido del eje).
  const largo2d =
    md == null && !esBloque && target.medidoEnMetros === true ? target.cantidadDefault : null;
  // Magnitudes del principal, resueltas como hasta ahora (área de muro = largo × alto es la
  // medida propia del elemento, no una relación de derivación).
  // Muro con huecos: el área es la MISMA cuenta que la del principal (bruto − huecos con su alto).
  const areaMuro =
    (md == null && target.muroVanos ? o.cuentaVanos?.netoM2 ?? null : null) ??
    target.areaMuroPorPlantas ?? (largo2d != null && alto > 0 ? largo2d * alto : null);
  const areaM2 =
    md
      ? md.area ?? null
      : target.esMuroDobleTrazo
        ? areaMuro
        : target.areaM2 != null && target.areaM2 > 0
          ? target.areaM2
          : areaMuro;
  const volumen = md
    ? md.volumen ?? null
    : areaMuro != null && espesor > 0 && (target.esMuroDobleTrazo || !(target.areaM2 != null && target.areaM2 > 0))
      ? areaMuro * espesor
      : null;
  const mags = {
    conteo: md ? md.conteo : esBloque ? target.cantidadDefault : null,
    largo: md ? md.longitud ?? null : largo2d,
    largoConVanos: md == null ? o.largoConVanosM : null,
    area: areaM2,
    volumen,
    vanos: vanosDelElemento(target, o.capas),
  };
  return mags;
}
